/**
 * ISO 4217 numeric code to alphabetic code.
 *
 * The API sends numeric currency codes, because that is what
 * ENUM_SYS_CurrencyOption stores; Intl.NumberFormat wants the letters. Without
 * this an amount can only be shown as a bare number, which reads as an amount in
 * whatever currency the viewer assumes.
 *
 * Generated from ENUM_SYS_CurrencyOption in OlaCore. Two members are not
 * three-letter codes: Unknown (1), which is deliberately absent here because it
 * is the absence of a currency, and AlbanianLek (8), which is ALL.
 */
export const CURRENCY_ALPHA: Record<number, string> = {
  8: "ALL",
  12: "DZD",
  32: "ARS",
  36: "AUD",
  44: "BSD",
  48: "BHD",
  50: "BDT",
  51: "AMD",
  52: "BBD",
  60: "BMD",
  64: "BTN",
  68: "BOB",
  72: "BWP",
  84: "BZD",
  90: "SBD",
  96: "BND",
  104: "MMK",
  108: "BIF",
  116: "KHR",
  124: "CAD",
  132: "CVE",
  136: "KYD",
  144: "LKR",
  152: "CLP",
  156: "CNY",
  170: "COP",
  174: "KMF",
  188: "CRC",
  191: "HRK",
  192: "CUP",
  203: "CZK",
  208: "DKK",
  214: "DOP",
  222: "SVC",
  230: "ETB",
  232: "ERN",
  238: "FKP",
  242: "FJD",
  262: "DJF",
  270: "GMD",
  292: "GIP",
  320: "GTQ",
  324: "GNF",
  328: "GYD",
  332: "HTG",
  340: "HNL",
  344: "HKD",
  348: "HUF",
  352: "ISK",
  356: "INR",
  360: "IDR",
  364: "IRR",
  368: "IQD",
  376: "ILS",
  388: "JMD",
  392: "JPY",
  398: "KZT",
  400: "JOD",
  404: "KES",
  408: "KPW",
  410: "KRW",
  414: "KWD",
  417: "KGS",
  418: "LAK",
  422: "LBP",
  426: "LSL",
  430: "LRD",
  434: "LYD",
  446: "MOP",
  454: "MWK",
  458: "MYR",
  462: "MVR",
  478: "MRO",
  480: "MUR",
  484: "MXN",
  496: "MNT",
  498: "MDL",
  504: "MAD",
  512: "OMR",
  516: "NAD",
  524: "NPR",
  532: "ANG",
  533: "AWG",
  548: "VUV",
  554: "NZD",
  558: "NIO",
  566: "NGN",
  578: "NOK",
  586: "PKR",
  590: "PAB",
  598: "PGK",
  600: "PYG",
  604: "PEN",
  608: "PHP",
  634: "QAR",
  643: "RUB",
  646: "RWF",
  654: "SHP",
  678: "STD",
  682: "SAR",
  690: "SCR",
  694: "SLL",
  702: "SGD",
  704: "VND",
  706: "SOS",
  710: "ZAR",
  728: "SSP",
  748: "SZL",
  752: "SEK",
  756: "CHF",
  760: "SYP",
  764: "THB",
  776: "TOP",
  780: "TTD",
  784: "AED",
  788: "TND",
  800: "UGX",
  807: "MKD",
  818: "EGP",
  826: "GBP",
  834: "TZS",
  840: "USD",
  858: "UYU",
  860: "UZS",
  882: "WST",
  886: "YER",
  901: "TWD",
  931: "CUC",
  932: "ZWL",
  933: "BYN",
  934: "TMT",
  936: "GHS",
  937: "VEF",
  938: "SDG",
  940: "UYI",
  941: "RSD",
  943: "MZN",
  944: "AZN",
  946: "RON",
  947: "CHE",
  948: "CHW",
  949: "TRY",
  950: "XAF",
  951: "XCD",
  952: "XOF",
  953: "XPF",
  955: "XBA",
  956: "XBB",
  957: "XBC",
  958: "XBD",
  959: "XAU",
  960: "XDR",
  961: "XAG",
  962: "XPT",
  963: "XTS",
  964: "XPD",
  965: "XUA",
  967: "ZMW",
  968: "SRD",
  969: "MGA",
  970: "COU",
  971: "AFN",
  972: "TJS",
  973: "AOA",
  974: "BYR",
  975: "BGN",
  976: "CDF",
  977: "BAM",
  978: "EUR",
  979: "MXV",
  980: "UAH",
  981: "GEL",
  984: "BOV",
  985: "PLN",
  986: "BRL",
  990: "CLF",
  994: "XSU",
  997: "USN",
  999: "XXX",
};

/**
 * Formats money for display. Falls back rather than guessing: an amount whose
 * currency is unknown is shown as a plain number, because printing it against the
 * wrong symbol states something false about how much it is.
 */
export function formatMoney(
  amount: number | undefined,
  currency: number | undefined,
  locale?: string
): string {
  if (amount == null || Number.isNaN(amount)) return "—";

  const alpha = currency != null ? CURRENCY_ALPHA[currency] : undefined;
  if (!alpha) return amount.toFixed(2);

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: alpha,
    }).format(amount);
  } catch {
    // A code Intl does not recognise - withdrawn currencies exist in the enum.
    // The code beside the number is still true, which the symbol would not be.
    return `${amount.toFixed(2)} ${alpha}`;
  }
}
